import { useParams, useLocation } from "react-router-dom";
import { getArticleBySlugSync } from "@/data/articles/index";
import NotFound from "@/pages/NotFound";
import PremiumRoute from "@/components/premium/PremiumRoute";
import { useAuth } from "@/context/AuthContext";
import { useTimedAccess } from "@/context/TimedAccessContext";
import { useArticleLoader } from "@/hooks/useArticleLoader";
import ArticleLoadingSkeleton from "@/components/article/ArticleLoadingSkeleton";
import ArticleRenderer from "@/components/article/ArticleRenderer";
import ErrorState from "@/components/article/ErrorState";
import { useEffect } from "react";
import { trackLoadingError } from "@/utils/analyticsTracker";

const ArticlePage = () => {
  // Extract slug directly from useParams and ensure it's a string
  const params = useParams<{ slug: string }>();
  const location = useLocation();
  
  // Handle both direct route and parameterized route
  let slug = params.slug;
  
  // Fallback for manually extracting slug if useParams fails
  if (!slug) {
    const pathParts = location.pathname.split('/');
    slug = pathParts[pathParts.length - 1];
  }
  
  console.log("ArticlePage - Slug extracted:", slug);
  
  const { isSubscribed, user } = useAuth();
  const { hasFreeAccess } = useTimedAccess();
  const searchParams = new URLSearchParams(window.location.search);
  const isPreviewMode = searchParams.get('preview') === 'true' || searchParams.get('test') === 'true';
  
  console.log("ArticlePage - FULL DEBUG:", {
    slug,
    user: user ? user.id : 'No user',
    isSubscribed,
    isPreviewMode,
    hasFreeAccess
  });
  
  // Get article metadata synchronously first for immediate checks
  const articleMeta = slug ? getArticleBySlugSync(slug) : undefined;
  
  // Special case for harmful-content-detection to force article metadata
  if (slug === 'harmful-content-detection' && !articleMeta) {
    console.log("ArticlePage - Forcing metadata for harmful-content-detection");
  }
  
  // Load article data using custom hook - removed forcePreviewMode parameter
  const { article, articleMeta: loadedArticleMeta, loading, loadError } = useArticleLoader(slug);
  
  // Log content loading progress 
  useEffect(() => {
    console.log("ArticlePage - Article loading state:", {
      slug,
      loading,
      hasError: !!loadError,
      hasArticleMeta: !!loadedArticleMeta,
      hasArticleContent: !!article?.content,
      contentLength: article?.content?.length || 0
    });
  }, [slug, loading, loadError, loadedArticleMeta, article]);
  
  useEffect(() => {
    if (loadError && slug) {
      trackLoadingError('article_load_failed', {
        pagePath: `/article/${slug}`,
        userId: user?.id || null,
        userEmail: user?.email || null,
        extraData: {
          slug,
          errorMessage: typeof loadError === 'string' ? loadError : (loadError as Error)?.message || 'Unknown error',
          component: 'ArticlePage',
        }
      })
    }
  }, [loadError, slug, user]);
  
  console.log("ArticlePage - Article metadata:", {
    found: !!loadedArticleMeta,
    title: loadedArticleMeta?.title,
    isPremium: loadedArticleMeta?.isPremium,
    comingSoon: loadedArticleMeta?.comingSoon,
    contentPath: loadedArticleMeta?.contentPath,
    hasDirectContent: !!loadedArticleMeta?.content,
    contentLength: loadedArticleMeta?.content?.length || 0,
    slug,
    isPreviewMode,
    hasFreeAccess
  });
  
  // Show improved loading state - add a more robust loading check
  if (loading || !article?.content) {
    return <ArticleLoadingSkeleton />;
  }
  
  // Special handling for harmful-content-detection if meta exists but no article
  if (slug === 'harmful-content-detection' && (!loadedArticleMeta || !article)) {
    console.log("ArticlePage - Attempting recovery for harmful-content-detection article");
    return (
      <ErrorState message="There was an issue loading the Harmful Content Detection article. Our team has been notified." />
    );
  }
  
  // Handle error states
  if (loadError || !loadedArticleMeta || !article) {
    console.log("ArticlePage - Article not found or error:", {
      loadError,
      articleMeta: !!loadedArticleMeta,
      article: !!article,
      slug
    });
    return <NotFound />;
  }
  
  // Handle "coming soon" articles
  if (loadedArticleMeta.comingSoon) {
    console.log("ArticlePage - Article is marked as coming soon:", slug);
    return <NotFound />;
  }
  
  // Get previous article for navigation if available
  const previousArticle = loadedArticleMeta.previousArticleSlug ? 
    getArticleBySlugSync(loadedArticleMeta.previousArticleSlug) : undefined;
  
  const previousArticleLink = previousArticle ? `/articles/${previousArticle.slug}` : undefined;
  const previousArticleTitle = previousArticle ? previousArticle.title : undefined;
  
  // Regular premium content flow (requires subscription or free access)
  if (loadedArticleMeta.isPremium === true && !isSubscribed && !hasFreeAccess && !isPreviewMode) {
    console.log("ArticlePage - Premium article, user not subscribed and no free access:", {
      isPremium: loadedArticleMeta.isPremium,
      isSubscribed,
      hasFreeAccess,
      isPreviewMode,
      slug
    });
    
    return (
      <PremiumRoute 
        element={
          <ArticleRenderer
            article={article}
            previousArticleLink={previousArticleLink}
            previousArticleTitle={previousArticleTitle}
            isPremium={article.isPremium}
            isPreviewMode={false}
          />
        }
      />
    );
  }
  
  // Standard article rendering (non-premium or user is subscribed or has free access)
  return (
    <ArticleRenderer
      article={article}
      previousArticleLink={previousArticleLink}
      previousArticleTitle={previousArticleTitle}
      isPremium={article.isPremium}
      isPreviewMode={loadedArticleMeta.isPremium && !isSubscribed && !hasFreeAccess && isPreviewMode}
    />
  );
};

export default ArticlePage;
